import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { I18N_LANGUAGE_KEY, I18N_MANUAL_SELECTION_KEY } from '../constants/i18n';

// Custom language detector that checks backend API for geolocation-based language
const geoLanguageDetector = {
  name: 'geoLanguageDetector',
  
  lookup() {
    // Synchronous lookup - return undefined to let async detect handle it
    return undefined;
  },
  
  async: true,
  
  async detect(callback: (lng: string) => void) {
    try {
      // Check if user has a manual language selection flag
      const manualSelection = localStorage.getItem(I18N_MANUAL_SELECTION_KEY);
      const storedLang = localStorage.getItem(I18N_LANGUAGE_KEY);
      
      // Only respect stored language if it was manually selected by user
      if (manualSelection === 'true' && storedLang && (storedLang === 'es' || storedLang === 'en')) {
        console.log('Using manually selected language:', storedLang);
        callback(storedLang);
        return;
      }
      
      // Otherwise, always detect based on geolocation
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5051/api';
      const response = await fetch(`${apiUrl}/detect-language`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const detectedLang = data.language || 'en';
        console.log('Language detected via geolocation:', detectedLang, 'Country:', data.country);
        callback(detectedLang);
      } else {
        // Fallback to English for international users
        console.warn('Language detection API failed, defaulting to English');
        callback('en');
      }
    } catch (error) {
      console.warn('Failed to detect language from geolocation:', error);
      // Fallback to English for international users
      callback('en');
    }
  },
  
  cacheUserLanguage(lng: string) {
    localStorage.setItem(I18N_LANGUAGE_KEY, lng);
    // Mark that this was a manual selection by the user
    localStorage.setItem(I18N_MANUAL_SELECTION_KEY, 'true');
  }
};


// Direct translations - guaranteed to work
const esTranslations = {
  test: "Prueba funcionando",
  navigation: {
    services: "Servicios",
    pricing: "Precios",
    about: "Nosotros",
    contact: "Contacto",
    login: "Entrar",
    register: "Registrarse"
  },
  nav: {
    dashboard: "Dashboard",
    agenda: "Agenda",
    teams: "Equipos",
    training: "Entrenamiento",
    sporting: "Área Deportiva",
    medical: "Área Médica",
    player: "Jugadores",
    messages: "Mensajes",
    settings: "Configuración",
    logout: "Cerrar Sesión"
  },
  common: {
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    cancel: "Cancelar",
    save: "Guardar",
    edit: "Editar",
    delete: "Eliminar",
    search: "Buscar",
    filter: "Filtrar",
    clear: "Limpiar",
    back: "Atrás",
    next: "Siguiente",
    previous: "Anterior",
    close: "Cerrar",
    open: "Abrir",
    view: "Ver",
    download: "Descargar",
    upload: "Subir",
    send: "Enviar",
    submit: "Enviar",
    confirm: "Confirmar",
    yes: "Sí",
    no: "No",
    hide: "Ocultar",
    expand: "Expandir",
    collapse: "Colapsar",
    user: "Usuario",
    comingSoon: "Próximamente"
  },
  settings: {
    security: "Seguridad de la cuenta",
    securityDescription: "Gestiona la seguridad de tu sesión",
    logoutWarning: "Cerrar sesión",
    logoutWarningText: "Cerrar sesión perderá el acceso a tu cuenta hasta que inicies sesión nuevamente.",
    currentSession: "Sesión actual",
    currentSessionText: "Cerrar sesión en este dispositivo",
    quickLogout: "Cierre de sesión rápido",
    quickLogoutText: "Cerrar sesión sin confirmación"
  },
  landing: {
    hero: {
      badge: "Consultoría en Sport Tech",
      title: "Consultoría en",
      titleHighlight: "Sport Tech",
      subtitle: "Optimiza tus procesos, ahorra costes y mantén una visión global del club en todo momento",
      description: "Transformo clubes deportivos con soluciones tecnológicas a medida. Especialista en IA para scouting, integración de datos y desarrollo digital personalizado.",
      startNow: "Contáctame",
      watchDemo: "Solicitar Información"
    },
    consultancy: {
      title: "Servicios de",
      titleHighlight: "Consultoría",
      subtitle: "Soluciones tecnológicas especializadas para clubes deportivos",
      aiScouting: {
        title: "Buscador IA Scouting",
        description: "Desarrollo de sistemas de búsqueda inteligente de jugadores con IA conversacional y análisis predictivo"
      },
      dataIntegration: {
        title: "Integración de Datos",
        description: "Conecta y centraliza todas las fuentes de datos de tu club para decisiones más informadas"
      },
      customDevelopment: {
        title: "Desarrollo Digital a Medida",
        description: "Aplicaciones web y móviles personalizadas para las necesidades específicas de tu organización"
      },
      training: {
        title: "Formación Especializada",
        description: "Capacitación en tecnologías deportivas y herramientas de análisis para tu equipo técnico"
      }
    },
    benefits: {
      title: "Beneficios para",
      titleHighlight: "tu Club",
      subtitle: "Impacto práctico directo en tu organización deportiva",
      improvedDecisions: {
        title: "Mejora en Decisiones",
        description: "Datos precisos y análisis avanzado para fichajes más acertados"
      },
      timeSaving: {
        title: "Ahorro de Tiempo",
        description: "Automatización de procesos repetitivos y búsquedas más eficientes"
      },
      customSolutions: {
        title: "Soluciones Adaptadas",
        description: "Desarrollo personalizado que se adapta exactamente a tus necesidades"
      }
    },
    about: {
      title: "Especialista en",
      titleHighlight: "Sport Tech",
      subtitle: "Experiencia real en tecnología deportiva y desarrollo de soluciones innovadoras",
      description: "Con amplia experiencia en el desarrollo de plataformas deportivas y análisis de datos, ayudo a clubes a aprovechar al máximo la tecnología para mejorar su rendimiento y toma de decisiones.",
      expertise: [
        "Desarrollo de sistemas de scouting con IA",
        "Integración de datos deportivos",
        "Análisis predictivo y machine learning",
        "Desarrollo web y mobile especializado"
      ]
    },
    aboutSection: { 
      title: "Sobre",
      titleHighlight: "ScoutMind AI",
      subtitle: "Somos pioneros en la aplicación de inteligencia artificial al scouting deportivo",
      mission: {
        title: "Nuestra Misión",
        description: "Democratizar el acceso a tecnología avanzada de scouting deportivo, proporcionando herramientas de IA que permitan a clubes de todos los niveles identificar y desarrollar talento."
      },
      vision: {
        title: "Nuestra Visión",
        description: "Ser la plataforma líder mundial en análisis deportivo con IA, revolucionando la industria del fútbol mediante tecnología innovadora."
      },
      ai: {
        title: "Inteligencia Artificial",
        description: "Algoritmos avanzados de machine learning para análisis predictivo y reconocimiento de patrones."
      },
      videoAnalysis: {
        title: "Análisis de Video",
        description: "YOLO v8 y OpenCV para detección automática y seguimiento de jugadores en tiempo real."
      },
      professionalScouting: {
        title: "Scouting Profesional",
        description: "Herramientas profesionales utilizadas por más de 500 clubes en todo el mundo."
      }
    },
    finalCta: {
      title: "¿Listo para transformar tu club?",
      subtitle: "Agenda una reunión gratuita y descubre cómo puedo ayudarte",
      scheduleButton: "Agendar Reunión Gratuita", 
      requestInfo: "Solicitar Información",
      packagesTitle: "Consulta Nuestras Tarifas",
      packagesSubtitle: "Cada proyecto es único. Solicita información personalizada para tu club."
    },
    stats: {
      playersAnalyzed: "Jugadores Analizados",
      activeClubs: "Clubes Activos",
      videoHours: "Horas de Video",
      aiAccuracy: "Precisión IA"
    },
    features: {
      title: "Tecnología de",
      titleHighlight: "Vanguardia",
      subtitle: "Descubre cómo la inteligencia artificial está transformando el scouting deportivo",
      conversationalAI: {
        title: "IA Conversacional",
        description: "Búsqueda inteligente de jugadores usando lenguaje natural"
      },
      videoAnalysis: {
        title: "Análisis de Video",
        description: "Análisis automático con Deep Learning y YOLO v8"
      },
      advancedAnalytics: {
        title: "Analytics Avanzado",
        description: "Machine Learning y análisis predictivo de rendimiento"
      },
      intelligentScouting: {
        title: "Scouting Inteligente",
        description: "IA para descubrimiento de talento futbolístico"
      }
    },
    pricing: {
      title: "Planes y",
      titleHighlight: "Precios",
      subtitle: "Elige el plan perfecto para tu organización deportiva",
      monthly: "Mensual",
      yearly: "Anual",
      save: "Ahorra 20%",
      mostPopular: "Más Popular",
      getStarted: "Comenzar",
      month: "mes",
      year: "año",
      saveUpTo: "Ahorra hasta 20%",
      saveVsMonthly: "Ahorra {{savings}}% en comparación con el plan mensual",
      detailedComparison: "Comparación detallada de características",
      allFeatures: "Todas las características incluidas en cada plan",
      categories: {
        ai: {
          title: "Inteligencia Artificial",
          features: [
            "Búsqueda con IA conversacional",
            "Análisis predictivo de rendimiento",
            "Recomendaciones inteligentes",
            "Procesamiento de lenguaje natural"
          ]
        },
        video: {
          title: "Análisis de Video",
          features: [
            "Detección automática de jugadores",
            "Métricas físicas en tiempo real",
            "Análisis de movimiento con YOLO",
            "Visualizaciones interactivas"
          ]
        },
        support: {
          title: "Soporte y Seguridad",
          features: [
            "Encriptación de datos",
            "Copias de seguridad automáticas",
            "Soporte técnico especializado",
            "Actualizaciones incluidas"
          ]
        }
      },
      plans: {
        starter: {
          name: "Starter",
          description: "Perfecto para clubes pequeños",
          features: {
            "0": "Hasta 50 jugadores",
            "1": "Búsqueda básica",
            "2": "Informes básicos",
            "3": "Soporte email"
          }
        },
        professional: {
          name: "Professional",
          description: "Para clubes profesionales",
          features: {
            "0": "Hasta 500 jugadores",
            "1": "IA Conversacional",
            "2": "Análisis avanzado",
            "3": "Soporte prioritario"
          }
        },
        enterprise: {
          name: "Enterprise",
          description: "Solución completa",
          features: {
            "0": "Jugadores ilimitados",
            "1": "Análisis completo de video",
            "2": "ML completo",
            "3": "Soporte 24/7"
          }
        }
      }
    },
    contact: {
      title: "Contacta con",
      titleHighlight: "Nosotros",
      subtitle: "¿Listo para revolucionar tu scouting? Hablemos sobre cómo ScoutMind AI puede ayudarte.",
      info: "Información de Contacto",
      email: "Email",
      phone: "Teléfono",
      schedule: "Lun-Vie: 9:00 - 18:00 CET",
      offices: "Oficinas",
      location: "A Coruña, España",
      readyToStart: "¿Listo para Empezar?",
      joinDescription: "Únete a ScoutMind AI para descubrir y desarrollar el talento del futuro.",
      startFreeTrial: "Comenzar Prueba Gratuita",
      viewAllPlans: "Ver Todos los Planes"
    },
    footer: {
      tagline: "Revolucionando el scouting deportivo con inteligencia artificial",
      copyright: "© 2025 ScoutMind AI. Todos los derechos reservados."
    },
    aiSearchDemo: {
      searchPlaceholder: "Ej: Jugadores con gol esperado mayor que...",
      searchButton: "Buscar con IA",
      searching: "Buscando...",
      demoComplete: "Demo Completa",
      exampleQueriesTitle: "Ejemplos de búsqueda:",
      exampleQueries: [
        "Encuentra defensas altos",
        "Jugadores menores de 20 años",
        "Defensa central de menos de 25 años"
      ],
      noResultsFound: "No se encontraron jugadores para esta búsqueda",
      demoCompleted: {
        title: "Demo completada",
        description: "¿Quieres explorar más? Contacta para cualquier problemática tecnológica que tengas.",
        contactButton: "Quiero Contactar"
      },
      mockDataWarning: {
        title: "Datos de demostración",
        description: "No se pudo completar la búsqueda. Mostrando resultados genéricos de ejemplo."
      }
    },
    videoDemo: {
      title: "Descubre lo que somos capaces de crear en ScoutMind AI",
      description1: "Aquí podrás ver cómo integramos inteligencia artificial, datos deportivos y analítica avanzada para convertir la información en conocimiento útil y decisiones estratégicas",
      description2: "Más de 93.000 jugadores, 2.200 clubes y 5,7 millones de registros procesados: rendimiento, traspasos, valoraciones, lesiones y mucho más",
      description3: "Diseñamos y desarrollamos soluciones digitales personalizadas para clubes, agencias y organizaciones que buscan llevar su scouting y gestión deportiva al siguiente nivel"
    },
    playerCard: {
      years: "Años",
      country: "País",
      height: "Altura",
      foot: "Pie",
      goals: "Goles",
      assists: "Asist."
    }
  },
  auth: {
    welcomeMessage: "Bienvenido de nuevo",
    enterCredentials: "Ingresa tus credenciales para continuar",
    username: "Usuario o Email",
    password: "Contraseña",
    forgotPassword: "¿Olvidaste tu contraseña?",
    login: "Iniciar Sesión",
    noAccount: "¿No tienes cuenta?",
    registerLink: "Regístrate",
    loginSuccess: "Inicio de sesión exitoso",
    loginError: "Error al iniciar sesión",
    logoutSuccess: "Sesión cerrada exitosamente",
    loginRequired: "Debes iniciar sesión para acceder",
    sessionExpired: "Tu sesión ha expirado",
    confirmLogout: "Confirmar cierre de sesión",
    logoutMessage: "¿Estás seguro de que quieres cerrar sesión? Necesitarás iniciar sesión de nuevo para acceder a tu cuenta.",
    forgotPasswordPage: {
      title: "¿Olvidaste tu contraseña?",
      subtitle: "Ingresa tu email y te enviaremos un enlace para restablecerla",
      email: "Email",
      emailPlaceholder: "tu@email.com",
      sendResetLink: "Enviar Enlace de Restablecimiento",
      sending: "Enviando...",
      backToLogin: "Volver al inicio de sesión",
      success: {
        title: "Solicitud Enviada",
        message: "Si el email {{email}} está registrado en nuestro sistema, recibirás un enlace de restablecimiento en unos minutos.",
        noEmailReceived: "¿No recibes el email?",
        tip1: "Revisa tu carpeta de spam o correo no deseado",
        tip2: "El enlace expira en 15 minutos",
        tip3: "Puedes solicitar uno nuevo si es necesario",
        sendNewLink: "Enviar Nuevo Enlace",
        backToLoginSuccess: "Volver al Inicio de Sesión"
      },
      errors: {
        emailRequired: "El email es requerido",
        emailInvalid: "Por favor ingresa un email válido",
        requestError: "Error al procesar la solicitud. Inténtalo de nuevo.",
        genericError: "Si el email existe, recibirás instrucciones de restablecimiento"
      }
    },
    register: {
      title: "Crear Cuenta",
      subtitle: "Únete a ScoutMind AI",
      firstName: "Nombre",
      lastName: "Apellidos",
      email: "Correo electrónico",
      password: "Contraseña",
      confirmPassword: "Confirmar contraseña",
      acceptTerms: "Acepto los términos y condiciones",
      registerButton: "Crear Cuenta",
      hasAccount: "¿Ya tienes cuenta?",
      signIn: "Inicia sesión aquí",
      registerSuccess: "Cuenta creada exitosamente",
      registerError: "Error al crear la cuenta"
    }
  },
  contactForm: {
    title: "Solicitar Información",
    subtitle: "Cuéntanos sobre tu proyecto y te contactaremos",
    fields: {
      name: "Nombre",
      namePlaceholder: "Tu nombre",
      lastName: "Apellidos", 
      lastNamePlaceholder: "Tus apellidos",
      club: "Club o Institución",
      clubPlaceholder: "Nombre del club o institución",
      role: "Rol",
      rolePlaceholder: "Selecciona tu rol",
      email: "Email",
      emailPlaceholder: "tu@email.com",
      phone: "Teléfono",
      phonePlaceholder: "Tu teléfono",
      message: "Mensaje",
      messagePlaceholder: "Cuéntanos más sobre tu proyecto o necesidades..."
    },
    roles: {
      agente: "Agente",
      director_deportivo: "Director Deportivo",
      scout: "Scout",
      directivo: "Directivo",
      otro: "Otros"

    },
    buttons: {
      submit: "Enviar Solicitud",
      sending: "Enviando..."
    },
    messages: {
      success: {
        title: "¡Solicitud enviada!",
        subtitle: "Nos pondremos en contacto contigo pronto."
      },
      error: {
        title: "Error al enviar",
        subtitle: "Por favor, inténtalo de nuevo."
      }
    }
  },
  app: {
    name: "ScoutMind AI",
    description: "Plataforma de scouting deportivo con inteligencia artificial"
  },
  dashboard: {
    title: "Dashboard",
    months:"meses",
    welcome: "Bienvenido a ScoutMind AI",
    subtitle: "Tu asistente de scouting con inteligencia artificial",
    searchPlaceholder: "Busca jugadores usando lenguaje natural...",
    searchButton: "Buscar",
    clearChat: "Limpiar chat",
    examplesTitle: "Ejemplos de búsqueda:",
    example1: "Busca delanteros jóvenes con buen regate",
    example2: "Encuentra defensas altos y fuertes",
    example3: "Jugadores rápidos para las bandas",
    searchBlocked: "Búsqueda anterior bloqueada",
    searchPlayerPlaceholder: "Describe qué jugador buscas... ej: 'delantero joven de menos de 23 años'",
    continueConversationPlaceholder: "Continúa la conversación o refina tu búsqueda...",
    filters: {
      title: "Filtros disponibles",
      age: "Edad",
      position: "Posición",
      team: "Equipo",
      league: "Liga",
      nationality: "Nacionalidad",
      value: "Valor de mercado",
      height: "Altura",
      foot: "Pie preferido"
    },
    results: {
      title: "Resultados de búsqueda",
      noResults: "No se encontraron jugadores",
      noResultsDescription: "Intenta ajustar tu búsqueda o realizar una nueva consulta",
      loading: "Buscando jugadores...",
      playersFound: "jugadores encontrados",
      viewProfile: "Ver perfil",
      addToList: "Agregar a lista"
    },
    chat: {
      thinking: "Analizando tu consulta...",
      error: "Error en la búsqueda",
      tryAgain: "Inténtalo de nuevo",
      searchQuery: "Consulta de búsqueda"
    },
    processed: "Procesado",
    aiAnalysis: {
      title: "Análisis IA avanzado de los resultados",
      generating: "Generando análisis avanzado de scouting...",
      regenerate: "Regenerar análisis",
      description: "Obtén un análisis profesional de estos {{count}} jugadores con IA",
      generate: "Generar Análisis IA"
    },
    pagination: {
      first: "Primera página",
      previous: "Página anterior",
      next: "Página siguiente",
      last: "Última página"
    },
    buttons: {
      sending: "Enviando...",
      send: "Enviar"
    },
    areas: {
      training: {
        title: "Area de Entrenamiento",
        description: "Gestión de entrenamientos y planificación deportiva"
      },
      teams: {
        title: "Area de Equipos",
        description: "Administración de equipos y plantillas"
      },
      sporting: {
        title: "Área Deportiva",
        description: "Scouting, análisis de jugadores y fichajes"
      },
      medical: {
        title: "Área Médica",
        description: "Seguimiento médico y prevención de lesiones"
      },
      personal: {
        title: "Personal",
        description: "Gestión de perfil y configuración personal"
      }
    },
    exploreAreas: "Explorar Áreas",
    recentActivity: "Actividad Reciente",
    noActivity: "No hay actividad reciente",
    noActivitySubtext: "Tus actividades aparecerán aquí próximamente"
  },
  sporting: {
    title: "Dirección",
    directionHighlight: "Deportiva",
    subtitle: "Gestión estratégica y scouting",
    advancedToolsText: "Herramientas avanzadas para la gestión estratégica",
    playerSearchIA: "Buscador IA de Jugadores",
    playerSearchIADescription: "Buscador inteligente de jugadores con IA conversacional",
    marketOpportunities: "Oportunidades del Mercado",
    expiringContractsDescription: "Jugadores con contratos próximos a vencer",
    playerComparison: "Comparación de Jugadores",
    playerComparisonDescription: "Compara dos jugadores en todas sus estadísticas y métricas",
    playersObserved: "Jugadores Observados",
    access: "Acceder",
    comingSoon: "Próximamente",
    aiPlayerSearch: "Buscador IA de Jugadores",
    aiPlayerSearchSubtitle: "Próximamente: Búsqueda inteligente con procesamiento de lenguaje natural",
    naturalSearch: "Búsqueda Natural",
    naturalSearchDescription: "Describe lo que buscas en lenguaje natural",
    intelligentAnalysis: "Análisis Inteligente",
    intelligentAnalysisDescription: "IA que comprende criterios complejos",
    preciseResults: "Resultados Precisos",
    preciseResultsDescription: "Encuentra jugadores que se ajusten perfectamente",
    moduleInDevelopment: "Módulo en Desarrollo",
    moduleComingSoon: "Esta funcionalidad estará disponible próximamente",
    backToSporting: "Volver a Dirección Deportiva"
  },
  contracts: {
    name: "Nombre",
    position: "Posición",
    team: "Equipo",
    league: "Liga",
    age: "Edad",
    contractEnd: "Fin Contrato",
    daysRemaining: "Días Restantes",
    marketValue: "Valor Mercado",
    status: "Estado",
    value: "Valor",
    actions: "Acciones",
    playersFound: "Jugadores Encontrados",
    unknownPlayer: "Jugador Desconocido",
    years: "años",
    expiredDaysAgo: "Vencido hace {{days}} días",
    thisSeason: "Esta temporada",
    oneYear: "En 1 año",
    longTerm: "Larga duración"
  },
  playerDetail: {
    intelligenceDashboard: "Dashboard de Inteligencia",
    trend: "Tendencia",
    predictionConfidence: "Confianza de la Predicción", 
    rise: "Alza",
    fall:"Caída",
    stable: "Estable",
    globalScore: "Puntuación Global",
    ageFactor: " Factor Edad en ROI",
    years: "Años",
    roiPrefix: "ROI",
    roi: "Retorno de la Inversión",
    expectedGrowth: "Crecimiento Experado", 
    next12months: "en los próximos 12 meses",
    roiCategory: "Caterogía ROI",
    roiMediumText: "Inversión de riesgo medio",
    roiHighText: "Inversión de riesgo alto",
    roiLowText: "Inversión de riesgo bajo",
    internationalExperience: "Experiencia Internacional",
    noInternationalExperience: "Sin Experiencia Internacional",
    back: "Volver",
    error: "Error",
    errorLoading: "Error al cargar el dashboard",
    errorLoadingIntelligence: "Error al cargar el dashboard de inteligencia",
    couldNotLoad: "No se pudo cargar el dashboard",
    player: "Jugador",
    overallScore: "Puntuación General",
    outOf100: "sobre 100",
    performance: "Rendimiento",
    value: "Valor",
    potential: "Potencial",
    availability: "Disponibilidad",
    recommendation: "Recomendación",
    recommended: "Recomendado",
    consider: "Considerar",
    notRecommended: "No Recomendado",
    undervaluedPlayer: "⚡ Jugador Infravalorado",
    contractStatus: "Estado Contractual",
    expires: "Vence",
    notAvailable: "No disponible",
    daysRemaining: "Días restantes",
    signingOpportunity: "Oportunidad de Fichaje",
    high: "ALTA",
    medium: "MEDIA",
    low: "BAJA",
    marketValueAnalysis: "Análisis de Valor de Mercado",
    currentValue: "Valor Actual",
    predictedValue: "Valor Predicho",
    valueEvolution: "Evolución del Valor",
    projectedEvolution: "Evolución Proyectada",
    performanceIndex: "Índice de Rendimiento",
    injuryRiskAssessment: "Evaluación de Riesgo de Lesiones",
    riskLevel: "Nivel de Riesgo",
    injuries: "Lesiones",
    daysLost: "Días Perdidos",
    average: "Promedio",
    recentInjuries: "Lesiones Recientes",
    days: "días",
    performanceSummary: "Resumen de Rendimiento por Temporada",
    season: "Temporada",
    competition: "Competición",
    matchesPlayed: "PJ",
    goals: "Goles",
    assists: "Asist.",
    minutes: "Minutos",
    transferHistory: "Historial de Traspasos",
    metricsOverview: "Resumen de Métricas",
    technicalIndex: "Índice Técnico",
    tacticalIndex: "Índice Táctico",
    physicalIndex: "Índice Físico",
    mentalIndex: "Índice Mental",
    decisionMaking: "Toma de Decisiones",
    creativity: "Creatividad",
    consistency: "Consistencia",
    leadership: "Liderazgo",
    versatility: "Versatilidad",
    gameIntelligence: "Inteligencia de Juego",
    summaryIndicators: "Indicadores Resumen",
    trendAnalysis: "Análisis de Tendencias",
    nextPotentialStep: "Próximo Paso Potencial",
    developmentProjection: "Proyección de Desarrollo",
    playerProfile: "Perfil del Jugador",
    estimatedMarketValue: "Valor de Mercado Estimado",
    growthPotential: "Potencial de Crecimiento",
    performanceRadar: "Radar de Rendimiento",
    opportunityWindow: "Ventana de Oportunidad",
    injuryHistory: "Historial de Lesiones",
    noInjuriesRecorded: "No hay lesiones registradas",
    formCurve: "Curva de Forma",
    lastMatches: "Últimos Partidos",
    reliabilityIndex: "Índice de Fiabilidad",
    stability: "Estabilidad",
    progression: "Progresión",
    estimatedPotential: "Potencial Estimado",
    efficiency: "Eficiencia",
    adaptability: "Adaptabilidad",
    keyStrengths: "Fortalezas Clave",
    keyWeaknesses: "Debilidades Clave"

  },
  comparison: {
    title: "Comparación de Jugadores",
    subtitle: "Selecciona dos jugadores para comparar sus atributos y estadísticas",
    selectPlayer: "Seleccionar Jugador",
    player1: "Jugador 1",
    player2: "Jugador 2",
    searchPlaceholder: "Buscar jugador por nombre...",
    noPlayersFound: "No se encontraron jugadores",
    selectPrompt: "Selecciona dos jugadores para continuar",
    selectFirstPlayer: "Selecciona el primer jugador para continuar",
    selectSecondPlayer: "Selecciona el segundo jugador para continuar",
    comparisonChart: "Gráfico de Comparación",
    attributesComparison: "Comparación de Atributos",
    detailedAnalysis: "Análisis Detallado",
    playerInfo: "Información del Jugador",
    basicInfo: "Información Básica",
    performance: "Rendimiento",
    marketValue: "Valor de Mercado",
    physical: "Físico",
    technical: "Técnico",
    goals: "Goles",
    assists: "Asistencias",
    xG: "xG (Goles Esperados)",
    xA: "xA (Asist. Esperadas)",
    progressions: "Progresiones",
    yellowCards: "Tarjetas Amarillas",
    redCards: "Tarjetas Rojas",
    matchesPlayed: "Partidos Jugados",
    minutesPlayed: "Minutos Jugados",
    goalsP90: "Goles/90'",
    assistsP90: "Asist./90'",
    age: "Edad",
    height: "Altura",
    position: "Posición",
    nationality: "Nacionalidad",
    team: "Equipo",
    league: "Liga",
    contract: "Contrato",
    foot: "Pie"
  }
};

const enTranslations = {
  test: "Test working",
  navigation: {
    services: "Services",
    pricing: "Pricing",
    about: "About",
    contact: "Contact",
    login: "Login",
    register: "Sign Up"
  },
  nav: {
    dashboard: "Dashboard",
    agenda: "Agenda",
    teams: "Teams",
    training: "Training",
    sporting: "Sporting Area",
    medical: "Medical Area",
    player: "Players",
    messages: "Messages",
    settings: "Settings",
    logout: "Logout"
  },
  common: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    save: "Save",
    edit: "Edit",
    delete: "Delete",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    back: "Back",
    next: "Next",
    previous: "Previous",
    close: "Close",
    open: "Open",
    view: "View",
    download: "Download",
    upload: "Upload",
    send: "Send",
    submit: "Submit",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    hide: "Hide",
    expand: "Expand",
    collapse: "Collapse",
    user: "User",
    comingSoon: "Coming Soon"
  },
  landing: {
    hero: {
      badge: "Sport Tech Consultancy",
      title: "Consultancy in",
      titleHighlight: "Sport Tech",
      subtitle: "Optimize your processes, reduce costs, and maintain a comprehensive view of your club at all times",
      description: "Transform sports clubs with custom technological solutions. Specialist in AI for scouting, data integration and personalized digital development.",
      startNow: "Contact Me",
      watchDemo: "Request Information"
    },
    consultancy: {
      title: "Consultancy",
      titleHighlight: "Services",
      subtitle: "Specialized technological solutions for sports clubs",
      aiScouting: {
        title: "AI Scouting Search",
        description: "Development of intelligent player search systems with conversational AI and predictive analysis"
      },
      dataIntegration: {
        title: "Data Integration",
        description: "Connect and centralize all your club's data sources for more informed decisions"
      },
      customDevelopment: {
        title: "Custom Digital Development",
        description: "Web and mobile applications customized for your organization's specific needs"
      },
      training: {
        title: "Specialized Training",
        description: "Training in sports technologies and analysis tools for your technical team"
      }
    },
    benefits: {
      title: "Benefits for",
      titleHighlight: "your Club",
      subtitle: "Direct practical impact on your sports organization",
      improvedDecisions: {
        title: "Improved Decisions",
        description: "Accurate data and advanced analysis for better signings"
      },
      timeSaving: {
        title: "Time Saving",
        description: "Automation of repetitive processes and more efficient searches"
      },
      customSolutions: {
        title: "Adapted Solutions",
        description: "Personalized development that adapts exactly to your needs"
      }
    },
    about: {
      title: "Specialist in",
      titleHighlight: "Sport Tech",
      subtitle: "Real experience in sports technology and development of innovative solutions",
      description: "With extensive experience in developing sports platforms and data analysis, I help clubs make the most of technology to improve their performance and decision-making.",
      expertise: [
        "Development of AI scouting systems",
        "Sports data integration",
        "Predictive analysis and machine learning",
        "Specialized web and mobile development"
      ]
    },
    aboutSection: {
      title: "About",
      titleHighlight: "ScoutMind AI",
      subtitle: "We are pioneers in applying artificial intelligence to sports scouting",
      mission: {
        title: "Our Mission",
        description: "Democratize access to advanced sports scouting technology, providing AI tools that enable clubs of all levels to identify and develop talent."
      },
      vision: {
        title: "Our Vision",
        description: "To be the world's leading platform in AI sports analysis, revolutionizing the football industry through innovative technology."
      },
      ai: {
        title: "Artificial Intelligence",
        description: "Advanced machine learning algorithms for predictive analysis and pattern recognition."
      },
      videoAnalysis: {
        title: "Video Analysis",
        description: "YOLO v8 and OpenCV for automatic detection and real-time player tracking."
      },
      professionalScouting: {
        title: "Professional Scouting",
        description: "Professional tools used by over 500 clubs worldwide."
      }
    },
    finalCta: {
      title: "Ready to transform your club?",
      subtitle: "Schedule a free meeting and discover how I can help you",
      scheduleButton: "Schedule Free Meeting",
      requestInfo: "Request Information",
      packagesTitle: "Consult Our Rates",
      packagesSubtitle: "Each project is unique. Request personalized information for your club."
    },
    stats: {
      playersAnalyzed: "Players Analyzed",
      activeClubs: "Active Clubs",
      videoHours: "Video Hours",
      aiAccuracy: "AI Accuracy"
    },
    features: {
      title: "Cutting-Edge",
      titleHighlight: "Technology",
      subtitle: "Discover how artificial intelligence is transforming sports scouting",
      conversationalAI: {
        title: "Conversational AI",
        description: "Intelligent player search using natural language"
      },
      videoAnalysis: {
        title: "Video Analysis",
        description: "Automatic analysis with Deep Learning and YOLO v8"
      },
      advancedAnalytics: {
        title: "Advanced Analytics",
        description: "Machine Learning and predictive performance analysis"
      },
      intelligentScouting: {
        title: "Intelligent Scouting",
        description: "AI for football talent discovery"
      }
    },
    pricing: {
      title: "Plans &",
      titleHighlight: "Pricing",
      subtitle: "Choose the perfect plan for your sports organization",
      monthly: "Monthly",
      yearly: "Yearly",
      save: "Save 20%",
      mostPopular: "Most Popular",
      getStarted: "Get Started",
      saveUpTo: "Save up to 20%",
      saveVsMonthly: "Save {{savings}}% vs monthly",
      detailedComparison: "Detailed Feature Comparison",
      allFeatures: "All features included in each plan",
      month: "month",
      year: "year",
      categories: {
        ai: {
          title: "Artificial Intelligence",
          features: [
            "Conversational AI search",
            "Predictive performance analysis",
            "Intelligent recommendations",
            "Natural language processing"
          ]
        },
        video: {
          title: "Video Analysis",
          features: [
            "Automatic player detection",
            "Real-time physical metrics",
            "Movement analysis with YOLO",
            "Interactive visualizations"
          ]
        },
        support: {
          title: "Support & Security",
          features: [
            "Data encryption",
            "Automatic backups",
            "Specialized technical support",
            "Updates included"
          ]
        }
      },
      plans: {
        starter: {
          name: "Starter",
          description: "Perfect for small clubs",
          features: {
            "0": "Up to 50 players",
            "1": "Basic search",
            "2": "Basic reports",
            "3": "Email support"
          }
        },
        professional: {
          name: "Professional",
          description: "For professional clubs",
          features: {
            "0": "Up to 500 players",
            "1": "Conversational AI",
            "2": "Advanced analysis",
            "3": "Priority support"
          }
        },
        enterprise: {
          name: "Enterprise",
          description: "Complete solution",
          features: {
            "0": "Unlimited players",
            "1": "Complete video analysis",
            "2": "Full ML",
            "3": "24/7 support"
          }
        }
      }
    },
    contact: {
      title: "Contact",
      titleHighlight: "Us",
      subtitle: "Ready to revolutionize your scouting? Let's talk about how ScoutMind AI can help you.",
      info: "Contact Information",
      email: "Email",
      phone: "Phone",
      schedule: "Mon-Fri: 9:00 - 18:00 CET",
      offices: "Offices",
      location: "A Coruña, Spain",
      readyToStart: "Ready to Get Started?",
      joinDescription: "Join ScoutMind AI to discover and develop the talent of the future.",
      startFreeTrial: "Start Free Trial",
      viewAllPlans: "View All Plans"
    },
    footer: {
      tagline: "Revolutionizing sports scouting with artificial intelligence",
      copyright: "© 2025 ScoutMind AI. All rights reserved."
    },
    aiSearchDemo: {
      searchPlaceholder: "e.g., Players with xG greater than...",
      searchButton: "Search with AI",
      searching: "Searching...",
      demoComplete: "Demo Complete",
      exampleQueriesTitle: "Search examples:",
      exampleQueries: [
        "Find tall defenders",
        "Players under 20 years old",
        "Central defender under 25 years"
      ],
      noResultsFound: "No players found for this search",
      demoCompleted: {
        title: "Demo completed",
        description: "Want to explore more? Contact us for any technology issues you have.",
        contactButton: "I Want to Contact"
      },
      mockDataWarning: {
        title: "Demo data",
        description: "Could not complete search. Showing generic example results."
      }
    },
    videoDemo: {
      title: "Explore the demo and discover what we are capable of creating at ScoutMind AI",
      description1: "Here you can see how we integrate artificial intelligence, sports data and advanced analytics to turn information into useful knowledge and strategic decisions",
      description2: "Over 93,000 players, 2,200 clubs and 5.7 million processed records: performance, transfers, valuations, injuries and much more",
      description3: "We design and develop customised digital solutions for clubs, agencies, and organisations looking to take their scouting and sports management to the next level"
    },
    playerCard: {
      years: "Years",
      country: "Country",
      height: "Height",
      foot: "Foot",
      goals: "Goals",
      assists: "Assists"
    }
  },
  auth: {
    welcomeMessage: "Welcome back",
    enterCredentials: "Enter your credentials to continue",
    username: "Username or Email",
    password: "Password",
    forgotPassword: "Forgot your password?",
    login: "Sign In",
    noAccount: "Don't have an account?",
    registerLink: "Sign up",
    loginSuccess: "Login successful",
    loginError: "Login error",
    logoutSuccess: "Successfully logged out",
    loginRequired: "You must log in to access",
    sessionExpired: "Your session has expired",
    confirmLogout: "Confirm Logout",
    logoutMessage: "Are you sure you want to log out? You will need to log in again to access your account.",
    forgotPasswordPage: {
      title: "Forgot your password?",
      subtitle: "Enter your email and we'll send you a reset link",
      email: "Email",
      emailPlaceholder: "your@email.com",
      sendResetLink: "Send Reset Link",
      sending: "Sending...",
      backToLogin: "Back to login",
      success: {
        title: "Request Sent",
        message: "If the email {{email}} is registered in our system, you will receive a reset link in a few minutes.",
        noEmailReceived: "Don't receive the email?",
        tip1: "Check your spam or junk mail folder",
        tip2: "The link expires in 15 minutes",
        tip3: "You can request a new one if needed",
        sendNewLink: "Send New Link",
        backToLoginSuccess: "Back to Sign In"
      },
      errors: {
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email",
        requestError: "Error processing request. Please try again.",
        genericError: "If the email exists, you will receive reset instructions"
      }
    },
    register: {
      title: "Create Account",
      subtitle: "Join ScoutMind AI",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email address",
      password: "Password",
      confirmPassword: "Confirm password",
      acceptTerms: "I accept the terms and conditions",
      registerButton: "Create Account",
      hasAccount: "Already have an account?",
      signIn: "Sign in here",
      registerSuccess: "Account created successfully",
      registerError: "Error creating account"
    }
  },
  contactForm: {
    title: "Request Information",
    subtitle: "Tell us about your project and we'll contact you",
    fields: {
      name: "Name",
      namePlaceholder: "Your name",
      lastName: "Last Name",
      lastNamePlaceholder: "Your last name",
      club: "Club or Institution",
      clubPlaceholder: "Club or institution name",
      role: "Role",
      rolePlaceholder: "Select your role",
      email: "Email",
      emailPlaceholder: "your@email.com",
      phone: "Phone",
      phonePlaceholder: "Your phone",
      message: "Message",
      messagePlaceholder: "Tell us more about your project or needs..."
    },
    roles: {
      agente: "Agent",
      director_deportivo: "Sports Director",
      scout: "Scout",
      directivo: "Executive",
      otro: "Others"
    },
    buttons: {
      submit: "Send Request",
      sending: "Sending..."
    },
    messages: {
      success: {
        title: "Request sent!",
        subtitle: "We will contact you soon."
      },
      error: {
        title: "Error sending",
        subtitle: "Please try again."
      }
    }
  },
  app: {
    name: "ScoutMind AI",
    description: "Sports scouting platform with artificial intelligence"
  },
  dashboard: {
    title: "Dashboard",
    months:"months",
    welcome: "Welcome to ScoutMind AI",
    subtitle: "Your AI-powered scouting assistant",
    searchPlaceholder: "Search players using natural language...",
    searchButton: "Search",
    clearChat: "Clear chat",
    examplesTitle: "Search examples:",
    example1: "Find young forwards with good dribbling",
    example2: "Look for tall and strong defenders",
    example3: "Fast players for the wings",
    searchBlocked: "Previous search blocked",
    searchPlayerPlaceholder: "Describe what player you're looking for... e.g. 'young striker under 23 years old'",
    continueConversationPlaceholder: "Continue the conversation or refine your search...",
    filters: {
      title: "Available filters",
      age: "Age",
      position: "Position",
      team: "Team",
      league: "League",
      nationality: "Nationality",
      value: "Market value",
      height: "Height",
      foot: "Preferred foot"
    },
    results: {
      title: "Search results",
      noResults: "No players found",
      noResultsDescription: "Try adjusting your search or make a new query",
      loading: "Searching players...",
      playersFound: "players found",
      viewProfile: "View profile",
      addToList: "Add to list"
    },
    chat: {
      thinking: "Analyzing your query...",
      error: "Search error",
      tryAgain: "Try again",
      searchQuery: "Search query"
    },
    processed: "Processed",
    aiAnalysis: {
      title: "Advanced AI analysis of results",
      generating: "Generating advanced scouting analysis...",
      regenerate: "Regenerate analysis",
      description: "Get a professional analysis of these {{count}} players with AI",
      generate: "Generate AI Analysis"
    },
    pagination: {
      first: "First page",
      previous: "Previous page",
      next: "Next page",
      last: "Last page"
    },
    buttons: {
      sending: "Sending...",
      send: "Send"
    },
    areas: {
      training: {
        title: "Training Area",
        description: "Training management and sports planning"
      },
      teams: {
        title: "Teams Area",
        description: "Team and squad administration"
      },
      sporting: {
        title: "Sporting Area",
        description: "Scouting, player analysis and transfers"
      },
      medical: {
        title: "Medical Area",
        description: "Medical monitoring and injury prevention"
      },
      personal: {
        title: "Personal",
        description: "Profile management and personal settings"
      }
    },
    exploreAreas: "Explore Areas",
    recentActivity: "Recent Activity",
    noActivity: "No recent activity",
    noActivitySubtext: "Your activities will appear here soon"
  },
  sporting: {
    title: "Sporting",
    directionHighlight: "Direction",
    subtitle: "Strategic management and scouting",
    advancedToolsText: "Advanced tools for strategic management",
    playerSearchIA: "AI Player Search",
    playerSearchIADescription: "Intelligent player search with conversational AI",
    marketOpportunities: "Market Opportunities",
    expiringContractsDescription: "Players with expiring contracts",
    playerComparison: "Player Comparison",
    playerComparisonDescription: "Compare two players across all statistics and metrics",
    playersObserved: "Players Observed",
    access: "Access",
    comingSoon: "Coming Soon",
    aiPlayerSearch: "AI Player Search",
    aiPlayerSearchSubtitle: "Coming soon: Intelligent search with natural language processing",
    naturalSearch: "Natural Search",
    naturalSearchDescription: "Describe what you're looking for in natural language",
    intelligentAnalysis: "Intelligent Analysis",
    intelligentAnalysisDescription: "AI that understands complex criteria",
    preciseResults: "Precise Results",
    preciseResultsDescription: "Find players that fit perfectly",
    moduleInDevelopment: "Module in Development",
    moduleComingSoon: "This functionality will be available soon",
    backToSporting: "Back to Sporting Direction"
  },
  contracts: {
    name: "Name",
    position: "Position",
    team: "Team",
    league: "League",
    age: "Age",
    contractEnd: "Contract End",
    daysRemaining: "Days Remaining",
    marketValue: "Market Value",
    status: "Status",
    value: "Value",
    actions: "Actions",
    playersFound: "Players Found",
    unknownPlayer: "Unknown Player",
    years: "years",
    expiredDaysAgo: "Expired {{days}} days ago",
    thisSeason: "This season",
    oneYear: "In 1 year",
    longTerm: "Long term"
  },
  playerDetail: {
    intelligenceDashboard: "Intelligence Dashboard",
    trend: "Trend",
    predictionConfidence: "Prediction Confidencie", 
    rise: "Rise",
    fall:"Fall",
    stable: "Stable",
    globalScore: "Overall score",
    ageFactor: " Age Factor in ROI",
    years: "Years",
    roiPrefix: "ROI",
    roi: "Return on Investment",
    expectedGrowth: "Expected Growth", 
    next12months: "in the next 12 months",
    roiCategory: "ROI Category",
    roiMediumText: "Medium Risk Investment",
    roiHighText: "High Risk Investment",
    roiLowText: "Low Risk Investment",
    internationalExperience: "International Experience",
    noInternationalExperience: "No International Experience",
    back: "Back",
    error: "Error",
    errorLoading: "Error loading dashboard",
    errorLoadingIntelligence: "Error loading intelligence dashboard",
    couldNotLoad: "Could not load dashboard",
    player: "Player",
    overallScore: "Overall Score",
    outOf100: "out of 100",
    performance: "Performance",
    value: "Value",
    potential: "Potential",
    availability: "Availability",
    recommendation: "Recommendation",
    recommended: "Recommended",
    consider: "Consider",
    notRecommended: "Not Recommended",
    undervaluedPlayer: "⚡ Undervalued Player",
    contractStatus: "Contract Status",
    expires: "Expires",
    notAvailable: "Not available",
    daysRemaining: "Days remaining",
    signingOpportunity: "Signing Opportunity",
    high: "HIGH",
    medium: "MEDIUM",
    low: "LOW",
    marketValueAnalysis: "Market Value Analysis",
    currentValue: "Current Value",
    predictedValue: "Predicted Value",
    valueEvolution: "Value Evolution",
    projectedEvolution: "Projected Evolution",
    performanceIndex: "Performance Index",
    injuryRiskAssessment: "Injury Risk Assessment",
    riskLevel: "Risk Level",
    injuries: "Injuries",
    daysLost: "Days Lost",
    average: "Average",
    recentInjuries: "Recent Injuries",
    days: "days",
    performanceSummary: "Performance Summary by Season",
    season: "Season",
    competition: "Competition",
    matchesPlayed: "MP",
    goals: "Goals",
    assists: "Assists",
    minutes: "Minutes",
    transferHistory: "Transfer History",
    metricsOverview: "Metrics Overview",
    technicalIndex: "Technical Index",
    tacticalIndex: "Tactical Index",
    physicalIndex: "Physical Index",
    mentalIndex: "Mental Index",
    decisionMaking: "Decision-Making",
    creativity: "Creativity",
    consistency: "Consistency",
    leadership: "Leadership",
    versatility: "Versatility",
    gameIntelligence: "Game Intelligence",
    summaryIndicators: "Summary Indicators",
    trendAnalysis: "Trend Analysis",
    nextPotentialStep: "Next Potential Step",
    developmentProjection: "Development Projection",
    playerProfile: "Player Profile",
    estimatedMarketValue: "Estimated Market Value",
    growthPotential: "Growth Potential",
    performanceRadar: "Performance Radar",
    opportunityWindow: "Opportunity Window",
    injuryHistory: "Injury History",
    noInjuriesRecorded: "No injuries recorded",
    formCurve: "Form Curve",
    lastMatches: "Last Matches",
    reliabilityIndex: "Reliability Index",
    stability: "Stability",
    progression: "Progression",
    estimatedPotential: "Estimated Potential",
    efficiency: "Efficiency",
    adaptability: "Adaptability",
    keyStrengths: "Key Strengths",
    keyWeaknesses: "Key Weaknesses"
      },
  comparison: {
    title: "Player Comparison",
    subtitle: "Select two players to compare their attributes and statistics",
    selectPlayer: "Select Player",
    player1: "Player 1",
    player2: "Player 2",
    searchPlaceholder: "Search player by name...",
    noPlayersFound: "No players found",
    selectPrompt: "Select two players to continue",
    selectFirstPlayer: "Select the first player to continue",
    selectSecondPlayer: "Select the second player to continue",
    comparisonChart: "Comparison Chart",
    attributesComparison: "Attributes Comparison",
    detailedAnalysis: "Detailed Analysis",
    playerInfo: "Player Information",
    basicInfo: "Basic Information",
    performance: "Performance",
    marketValue: "Market Value",
    physical: "Physical",
    technical: "Technical",
    goals: "Goals",
    assists: "Assists",
    xG: "xG (Expected Goals)",
    xA: "xA (Expected Assists)",
    progressions: "Progressions",
    yellowCards: "Yellow Cards",
    redCards: "Red Cards",
    matchesPlayed: "Matches Played",
    minutesPlayed: "Minutes Played",
    goalsP90: "Goals/90'",
    assistsP90: "Assists/90'",
    age: "Age",
    height: "Height",
    position: "Position",
    nationality: "Nationality",
    team: "Team",
    league: "League",
    contract: "Contract",
    foot: "Foot"
  }
};


console.log('I18N: Loading translations system...');


const resources = {
  es: { translation: esTranslations },
  'es-ES': { translation: esTranslations },
  en: { translation: enTranslations },
  'en-US': { translation: enTranslations }
};


// Force reinitialization to ensure translations load correctly
console.log('I18N: Force reinitializing with fresh resources...');

// Create a custom LanguageDetector with our geo detector
const customLanguageDetector = new LanguageDetector();
customLanguageDetector.addDetector(geoLanguageDetector);

const initPromise = i18n
  .use(customLanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false, // Disable debug now that it's working
    interpolation: {
      escapeValue: false
    },
    detection: {
      // Order: geo detector first, then localStorage, then browser language
      order: ['geoLanguageDetector', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    // Force reload
    initImmediate: false
  })
  .then(() => {
    // Always ensure resources are properly loaded
    i18n.addResourceBundle('es', 'translation', esTranslations, true, true);
    i18n.addResourceBundle('en', 'translation', enTranslations, true, true);
    return Promise.resolve();
  });


// Wait for initialization to complete
initPromise.then(() => {
  console.log('I18N: System initialized and resources loaded successfully');
});


export default i18n;
